import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Lock, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default category images - futuristic 3D style
const DEFAULT_IMAGES = {
  'offres-de-placement-livret': 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=600&q=80',
  'offres-exclusives-premium-access': 'https://images.unsplash.com/photo-1642104704074-907c0698cbd9?w=600&q=80',
  'offres-de-decouverte': 'https://images.unsplash.com/photo-1622630998477-20aa696ecb05?w=600&q=80',
  'mandats-de-gestion': 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=600&q=80',
  'default': 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=600&q=80'
};

// Color schemes for cards
const CARD_COLORS = [
  { bg: 'from-blue-900/80 to-blue-950/90', accent: 'bg-blue-500' },
  { bg: 'from-indigo-900/80 to-indigo-950/90', accent: 'bg-indigo-500' },
  { bg: 'from-orange-900/80 to-orange-950/90', accent: 'bg-orange-500' },
  { bg: 'from-emerald-900/80 to-emerald-950/90', accent: 'bg-emerald-500' },
];

export default function ClientPlacements() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lockModal, setLockModal] = useState(null);
  const [lockCode, setLockCode] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/placements/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(res.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const getImageUrl = (category) => {
    if (category.image_url) return category.image_url;
    return DEFAULT_IMAGES[category.slug] || DEFAULT_IMAGES.default;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white" data-testid="placements-title">Placements</h1>
          <p className="text-muted-foreground mt-2">Découvrez nos offres d&apos;investissement</p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => {
            const colorScheme = CARD_COLORS[index % CARD_COLORS.length];
            const isPremium = category.title?.toLowerCase().includes('premium') || 
                              category.title?.toLowerCase().includes('exclusiv');
            
            return (
              <Card 
                key={category.id}
                className={`overflow-hidden border-0 bg-gradient-to-br ${colorScheme.bg} hover:scale-[1.02] transition-transform cursor-pointer group`}
                onClick={() => {
                  if (category.is_locked) {
                    setLockModal(category);
                    setLockCode('');
                  } else {
                    navigate(`/client/placements/${category.id}`);
                  }
                }}
                data-testid={`category-card-${category.slug}`}
              >
                <CardContent className="p-0">
                  {/* Image Section */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={getImageUrl(category)}
                      alt={category.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* 3D Effect Overlay */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl ${colorScheme.accent} flex items-center justify-center shadow-lg`}>
                          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-6 space-y-4">
                    <div>
                      <h3 className="text-xl font-bold text-white uppercase tracking-wide">
                        {category.title}
                      </h3>
                      {category.description && (
                        <p className="text-sm text-gray-400 mt-2 line-clamp-2">
                          {category.description}
                        </p>
                      )}
                    </div>

                    {/* CTA Button */}
                    <Button 
                      className={`w-full ${category.is_locked ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-500 hover:bg-blue-600'} text-white font-medium`}
                      data-testid={`discover-btn-${category.slug}`}
                    >
                      {category.is_locked && <Lock className="w-4 h-4 mr-2" />}
                      Découvrir
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Empty State */}
        {categories.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto rounded-full bg-muted/20 flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Aucune offre disponible</h3>
            <p className="text-muted-foreground">Les placements seront bientôt disponibles.</p>
          </div>
        )}

        {/* Decorative Element - Crypto Coins */}
        <div className="fixed bottom-8 right-8 opacity-30 pointer-events-none hidden lg:block">
          <img 
            src="https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=300&q=60"
            alt=""
            className="w-48 h-48 object-cover rounded-full"
          />
        </div>
      </div>

      {/* Lock Password Modal */}
      <Dialog open={!!lockModal} onOpenChange={() => setLockModal(null)}>
        <DialogContent className="max-w-sm" data-testid="category-lock-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-500" />
              Accès protégé
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Cette catégorie nécessite un code d&apos;accès. Contactez votre conseiller pour l&apos;obtenir.
            </p>
            <Input
              type="password"
              value={lockCode}
              onChange={(e) => setLockCode(e.target.value)}
              placeholder="Entrez le code d'accès"
              data-testid="category-lock-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && lockCode && lockModal) {
                  axios.post(`${API}/clients/verify-placement-password`, {
                    category_id: lockModal.id, password: lockCode
                  }, { headers: { Authorization: `Bearer ${token}` } })
                    .then(() => { setLockModal(null); navigate(`/client/placements/${lockModal.id}`); })
                    .catch(() => toast.error('Code incorrect'));
                }
              }}
            />
            <Button
              className="w-full"
              onClick={async () => {
                if (!lockCode || !lockModal) return;
                try {
                  await axios.post(`${API}/clients/verify-placement-password`, {
                    category_id: lockModal.id, password: lockCode
                  }, { headers: { Authorization: `Bearer ${token}` } });
                  setLockModal(null);
                  navigate(`/client/placements/${lockModal.id}`);
                } catch { toast.error('Code incorrect'); }
              }}
              data-testid="category-lock-submit"
            >
              Accéder
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
